import * as React from 'react';
import { ForwardedRef, useEffect, useMemo, useReducer, useState } from 'react';

import { translate } from '~l10n/client';
import Backbone from 'backbone';
import cx from 'classnames';
import { useSetAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useSelector } from 'react-redux';

import { FeatureUseEventAdapter } from '~analytics/adapters/FeatureUseEventAdapter';
import { PaywallScreens } from '~analytics/types';
import { Badge } from '~shared/components/Badge';
import { SmallButton, UIButton } from '~shared/components/buttons';
import { DialogConfirmation } from '~shared/components/Dialog';
import { IconStudents } from '~shared/components/icons/standard';
import {
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuItemRenderPropArgs,
  MenuList,
} from '~shared/components/Menu';
import { toast } from '~shared/components/Toast';
import ToggleSwitch from '~shared/components/ToggleSwitch/ToggleSwitch';
import { HandRaiseIcon } from '~teacher/components/Header/StudentMenu/HandRaiseIcon';
import { useRoomsPaywallDispatch } from '~teacher/components/rooms/RoomsPaywall/RoomsPaywallProvider';
import { useRoomsList } from '~teacher/hook/room';
import {
  selectCurrentRoomActivity,
  selectCurrentRoomId,
} from '~teacher/slices/teacher/teacherSelector';
import { proCheck } from '~teacher/util/proCheck';

import platform from '../../../../../../../shared/Platform';
import { clearRoom } from '../../../../api/rooms/clearRoom';
import { assertUnreachable } from '../../../../util/assertUnreachable';

// legacy imports using require
const roomController = require('../../../../../teacher/rooms/controllers/RoomsController');
const roomModel = require('../../../../../teacher/rooms/models/RoomsModel');
const Constants = require('Constants');
const room = require('room');

const rosterModalAtomVisibility = atomWithStorage(
  Constants.ROSTER_MODAL,
  'none'
);
const shouldShowImportRoster = !platform.isAndroidApp && !platform.isIosApp;

interface StyledItemProps extends MenuItemRenderPropArgs {
  children: React.ReactNode;
  isTitle?: boolean;
  isPro?: boolean;
}
function StyledMenuItem({
  children,
  isTitle,
  isActive,
  ...props
}: StyledItemProps) {
  const buttonClass = cx(
    'flex w-full px-12 py-3 whitespace-wrap cursor-pointer',
    'font-sans text-base text-blue-900 leading-8 text-left border-0 rounded-1',
    isActive ? 'bg-cyan-50' : 'bg-white'
  );

  const titleClass = cx(
    'font-sans text-xs font-700 leading-8 text-grey-900 tracking-wider uppercase pl-12 pr-1 pt-4 pb-2 m-0 bg-white'
  );

  return (
    <div
      className={cx(
        'flex flex-row items-center rounded-1',
        isActive ? 'bg-cyan-50' : 'bg-white'
      )}
    >
      {isTitle ? (
        <div className="flex flex-row items-baseline">
          <h3 className={titleClass}>{children}</h3>
          {!proCheck.hasPaidPlan() && <Badge>Pro</Badge>}
        </div>
      ) : (
        <button className={buttonClass} type="button" {...props}>
          {children}
        </button>
      )}
    </div>
  );
}

interface AddStudentsMenuItemProps extends MenuItemRenderPropArgs {
  label: string;
  isVisible: boolean;
}

function AddStudentsMenuItem({
  label,
  isVisible,
  isActive,
  ...props
}: AddStudentsMenuItemProps) {
  if (!isVisible) return null;

  return (
    <StyledMenuItem isActive={isActive} {...props}>
      {label}
    </StyledMenuItem>
  );
}

interface ToggleMenuItemProps extends MenuItemRenderPropArgs {
  children: React.ReactNode;
  on: boolean;
  onChange: (newValue: boolean) => void;
}
function ToggleMenuItem({
  children,
  on,
  isActive,
  onChange,
  ...props
}: ToggleMenuItemProps) {
  const containerClass = cx(
    'flex justify-between w-full px-2 py-3 whitespace-wrap cursor-pointer',
    'font-sans text-base text-blue-900 leading-8 text-left border-0 rounded-1',
    isActive ? 'bg-cyan-50' : 'bg-white'
  );

  return (
    <label
      className={cx(
        'flex flex-row justify-center items-center rounded-1 px-10 py-2',
        isActive ? 'bg-cyan-50' : 'bg-white'
      )}
      {...props}
    >
      <div className={containerClass}>
        {children}
        <ToggleSwitch size="small" on={on} onChange={onChange} />
      </div>
    </label>
  );
}

interface HandRaiseMenuItemProps extends MenuItemRenderPropArgs {
  studentId: string;
  a11yLabel: string;
  name: string;
  isPresent: boolean;
  isStudentIdsVisible: boolean;
  allowHandraise: boolean;
  isHandRaised: boolean;
}
function HandRaiseMenuItem({
  studentId,
  a11yLabel,
  name,
  isPresent,
  isStudentIdsVisible,
  allowHandraise,
  isHandRaised,
  isActive,
  ...props
}: HandRaiseMenuItemProps) {
  const buttonClass = cx(
    'flex w-full justify-between px-12 py-3 whitespace-wrap',
    'font-sans text-base text-blue-900 leading-8 text-left border-0 rounded-1',
    isActive ? 'bg-cyan-50' : 'bg-white',
    isPresent && 'cursor-pointer'
  );
  return (
    <div
      className={cx(
        'flex flex-row items-center rounded-1',
        isActive ? 'bg-cyan-50' : 'bg-white'
      )}
    >
      <button
        aria-label={a11yLabel}
        className={buttonClass}
        type="button"
        {...props}
      >
        <div className="flex flex-col items-start">
          <p
            className={cx(
              'font-sans text-base leading-8 max-w-60 truncate',
              isPresent ? 'text-blue-900' : 'text-grey-600'
            )}
          >
            {name}
          </p>
          {isStudentIdsVisible && (
            <p
              className={cx(
                'font-sans text-xs leading-8 -mt-2',
                isPresent ? 'text-grey-900' : 'text-grey-600'
              )}
            >
              {`ID: ${studentId}`}
            </p>
          )}
        </div>
        {isPresent && allowHandraise && (
          <HandRaiseIcon isActive={isHandRaised} />
        )}
      </button>
    </div>
  );
}

interface StudentMenuProps {
  controller: any;
  model: any;
  handsRaised: number;
  studentList: any[];
  enableHandraise: boolean;
}

type ClearRoomConfirmationDialogAction =
  | { type: 'open'; currentRoomName: string }
  | { type: 'close' };

interface ClearRoomConfirmationDialogState {
  isOpen: boolean;
  currentRoomName: string;
}

function reducer(
  state: ClearRoomConfirmationDialogState,
  action: ClearRoomConfirmationDialogAction
) {
  switch (action.type) {
    case 'open': {
      const { currentRoomName } = action;
      return {
        isOpen: true,
        currentRoomName: currentRoomName,
      };
    }
    case 'close': {
      return {
        isOpen: false,
        currentRoomName: '',
      };
    }
    default:
      assertUnreachable(action);
  }
}

export function StudentMenu({
  controller,
  model,
  handsRaised,
  studentList,
  enableHandraise,
}: StudentMenuProps) {
  const paywallDispatch = useRoomsPaywallDispatch();
  const [isStudentIdsVisible, setIsStudentIdsVisible] = useState(true);
  const setRosterModalVisibility = useSetAtom(rosterModalAtomVisibility);
  const { isLive } = useSelector(selectCurrentRoomActivity);

  const roomId = useSelector(selectCurrentRoomId);
  const roomQuery = useRoomsList({
    select: (rooms) => rooms.find((room) => room.id === roomId),
  });
  const currentRoom = roomQuery.data;
  const roomName = currentRoom?.name;
  // Live student roster. Like the hand-raise badge below, the `studentList`
  // prop only refreshes when HeaderController re-renders, so a join/leave
  // (presence) event would not update the list on routes where the header is
  // not re-rendered. Messaging.presenceChanged already calls
  // room.set('students', ...) (firing `change:students`), so mirror that into
  // local state — no extra fetch.
  const [liveStudentList, setLiveStudentList] = useState(studentList);

  useEffect(() => {
    setLiveStudentList(studentList);
  }, [studentList]);

  useEffect(() => {
    const syncStudents = () => setLiveStudentList(room.getStudents());
    room.on('change:students', syncStudents);
    return () => room.off('change:students', syncStudents);
  }, []);

  const hasRoster = liveStudentList && liveStudentList.length > 0;
  const sortedStudentList = useMemo(() => {
    if (!liveStudentList) return [];
    return [...liveStudentList].sort((a, b) => {
      return b.logged_in - a.logged_in;
    });
  }, [liveStudentList]);
  const [{ currentRoomName, isOpen }, dispatch] = useReducer(reducer, {
    isOpen: false,
    currentRoomName: roomName ?? '',
  });

  // Live hand-raise badge. The header is mounted on every route, but the
  // `handsRaised` prop only refreshes when HeaderController re-renders — which
  // does not happen on a bare hand-raise event while off the Live Results page,
  // so the badge would otherwise go stale there. The global
  // HeaderController.handRaiseChanged listener still updates the shared `room`
  // Backbone model on every route (room.updateStudentHandraise ->
  // room.setHandsRaised), so we mirror the model's count into local state.
  // Listening to the model's `change:handsRaised` event (rather than a specific
  // PubNub key) is wire-key-agnostic and fires after the count is recomputed.
  const [liveHandsRaised, setLiveHandsRaised] = useState(handsRaised);

  useEffect(() => {
    setLiveHandsRaised(handsRaised);
  }, [handsRaised]);

  useEffect(() => {
    const syncHandsRaised = () => setLiveHandsRaised(room.getHandsRaised());
    room.on('change:handsRaised', syncHandsRaised);
    return () => room.off('change:handsRaised', syncHandsRaised);
  }, []);

  if (!roomQuery.data || (!sortedStudentList && hasRoster)) {
    return <div data-testid="student-menu">Loading...</div>;
  }

  const isAddingRosterVisible =
    !hasRoster || (currentRoom.showbieClassId && sortedStudentList.length < 1);
  const isStudentListVisible =
    proCheck.hasPaidPlan() && hasRoster && sortedStudentList.length > 0;

  const handleRoster = async (actionType: 'import' | 'manual') => {
    if (!proCheck.canAddRoster()) {
      paywallDispatch({
        type: 'open',
        payload: { paywallTouch: [PaywallScreens.AppHeaderAddRoster] },
      });
      return;
    }

    if (currentRoom.default) {
      toast.critical(
        translate('Rosters are not allowed in the default room.'),
        { disableClose: true }
      );
      return;
    }

    if (currentRoom.showbieClassId && studentList.length < 1) {
      toast(
        translate('This roster is synced to your Showbie class ‘{0}’.').format(
          currentRoom.showbieClassName
        ),
        { disableClose: true }
      );
      return;
    }

    if (isLive) {
      toast.critical(
        translate('A roster cannot be added while an activity is running.'),
        { disableClose: true }
      );
      return;
    }

    if (Backbone.history.getFragment() !== Constants.ROOMS_TAB) {
      setRosterModalVisibility(actionType);
      Backbone.history.navigate(Constants.ROOMS_TAB, { trigger: true });
    } else {
      roomModel.manageRoster(roomName);
      roomController.presenceChanged();
      actionType === 'import'
        ? roomController.importClicked(roomName)
        : roomModel.addStudentsManually();
    }

    switch (actionType) {
      case 'import': {
        new FeatureUseEventAdapter({
          feature: 'import_roster',
          action: 'click',
          source: 'new_header',
        }).sendAnalytics();
        break;
      }
      case 'manual': {
        new FeatureUseEventAdapter({
          feature: 'add_students',
          action: 'click',
          source: 'new_header',
        }).sendAnalytics();
        break;
      }
      default: {
        assertUnreachable(actionType);
      }
    }
  };

  const handleClearRoom = async () => {
    try {
      await clearRoom({
        roomName: currentRoomName,
      });
      model.studentsInRoom = 0;

      new FeatureUseEventAdapter({
        feature: 'clear_room',
        action: 'click',
        source: 'new_header',
      }).sendAnalytics();

      dispatch({ type: 'close' });
    } catch (err) {
      toast.critical(
        translate('The room could not be cleared. Please try again later.'),
        {
          disableClose: true,
        }
      );
      if (__DEV__) {
        console.error('Failed to clear room', err);
      }
    }
  };

  return (
    <div data-testid="student-menu" className="min-w-fit mr-3">
      <Menu>
        <MenuButton>
          {({ props }) => (
            <StudentMenuButton
              studentsInRoom={model.studentsInRoom}
              sortedStudentListLength={sortedStudentList.length}
              handsRaised={liveHandsRaised}
              hasRoster={hasRoster}
              {...props}
            />
          )}
        </MenuButton>
        <MenuList className="absolute w-100 sm:w-116 -right-120px md:right-0 px-2 sm:mx-auto py-3 bg-white rounded-2 outline-none shadow-menu max-h-screen-minus-28 overflow-y-scroll">
          <MenuItem
            onClick={() =>
              dispatch({ type: 'open', currentRoomName: roomName })
            }
          >
            {({ isActive, props }) => (
              <StyledMenuItem isActive={isActive} {...props}>
                {translate('Clear Students from Room')}
              </StyledMenuItem>
            )}
          </MenuItem>
          <MenuDivider />
          <MenuItem>
            {({ props }) => (
              <>
                <StyledMenuItem isActive={false} isTitle={true} {...props}>
                  {translate('Roster')}
                </StyledMenuItem>
                <p className="px-12 pb-4 text-xs leading-6 -mt-2 text-grey-900">
                  {translate(
                    'Manage and restrict access to your room and enable hand raising.'
                  )}
                </p>
              </>
            )}
          </MenuItem>
          <MenuItem onClick={async () => await handleRoster('import')}>
            {({ isActive, props }) => (
              <AddStudentsMenuItem
                isVisible={isAddingRosterVisible && shouldShowImportRoster}
                label={translate('Import Roster')}
                isActive={isActive}
                {...props}
              />
            )}
          </MenuItem>
          <MenuItem onClick={async () => await handleRoster('manual')}>
            {({ isActive, props }) => (
              <AddStudentsMenuItem
                isVisible={isAddingRosterVisible}
                label={translate('Manual (Type In)')}
                isActive={isActive}
                {...props}
              />
            )}
          </MenuItem>
          {isStudentListVisible && (
            <>
              <MenuItem dismissOnClick={false}>
                {({ isActive, props }) => (
                  <ToggleMenuItem
                    isActive={isActive}
                    on={enableHandraise}
                    onChange={controller.toggleHandraiseStatus}
                    {...props}
                  >
                    {translate('Enable Hand Raise')}
                  </ToggleMenuItem>
                )}
              </MenuItem>
              <MenuItem dismissOnClick={false}>
                {({ isActive, props }) => (
                  <ToggleMenuItem
                    isActive={isActive}
                    on={isStudentIdsVisible}
                    onChange={() => {
                      setIsStudentIdsVisible(!isStudentIdsVisible);
                    }}
                    {...props}
                  >
                    {translate('Show Student IDs')}
                  </ToggleMenuItem>
                )}
              </MenuItem>
              <MenuDivider />
              {sortedStudentList.map(
                ({
                  first_name,
                  last_name,
                  student_id,
                  handraise,
                  logged_in,
                  id,
                }) => (
                  <StudentMenuItem
                    key={id}
                    isStudentIdsVisible={isStudentIdsVisible}
                    enableHandRaise={enableHandraise}
                    isLoggedIn={logged_in}
                    lastName={last_name}
                    firstName={first_name}
                    studentId={student_id}
                    handRaise={handraise}
                    toggleStudentHandraise={(id: string) =>
                      controller.toggleStudentHandraise(id)
                    }
                    id={id}
                  />
                )
              )}
            </>
          )}
        </MenuList>
      </Menu>
      <DialogConfirmation
        closeLabel={translate('Close')}
        actions={[
          <UIButton
            onClick={() => {
              dispatch({ type: 'close' });
            }}
            key="cancel"
          >
            {translate('No')}
          </UIButton>,
          <UIButton
            appearance="primary"
            onClick={handleClearRoom}
            key="primary"
          >
            {translate('Yes')}
          </UIButton>,
        ]}
        open={isOpen}
        onClose={() => dispatch({ type: 'close' })}
        id="clear-room"
        title={translate('Please Confirm')}
      >
        {translate(
          'Are you sure you want to clear all students from the {0} room?'
        ).format(currentRoomName.toUpperCase())}
      </DialogConfirmation>
    </div>
  );
}

interface StudentMenuButtonProps {
  hasRoster: boolean;
  handsRaised: number;
  studentsInRoom: number;
  sortedStudentListLength: number;
}
const StudentMenuButton = React.forwardRef(function StudentMenuButton(
  {
    hasRoster,
    handsRaised,
    sortedStudentListLength,
    studentsInRoom,
    ...props
  }: StudentMenuButtonProps,
  ref: ForwardedRef<HTMLButtonElement>
) {
  const students = hasRoster
    ? translate('{0} of {1}').format(studentsInRoom, sortedStudentListLength)
    : `${studentsInRoom}`;

  const ariaLabel: string[] = [
    translate('Student Menu'),
    ...(hasRoster && handsRaised > 0
      ? [translate('{0} hand raised').format(handsRaised)]
      : []),
    hasRoster
      ? translate('{0} of {1} students').format(
          studentsInRoom,
          sortedStudentListLength
        )
      : translate('{0} students').format(studentsInRoom),
  ];

  return (
    <>
      <SmallButton
        a11yLabel={ariaLabel.join(', ')}
        className="bg-opacity-0"
        isOnWhite={true}
        appearance={'secondary'}
        ref={ref}
        {...props}
      >
        <IconStudents className="text-grey-900" />
        <p className="text-grey-900 text-sm font-600 ml-1">{students}</p>
      </SmallButton>
      {handsRaised > 0 ? (
        <span className="bg-red-800 text-white rounded-8 text-xs absolute top-4px right-0 px-2 mt-px transform translate-x-1/2 -translate-y-1/2 z-10">
          {handsRaised}
        </span>
      ) : null}
    </>
  );
});

interface StudentMenuItemProps {
  isStudentIdsVisible: boolean;
  enableHandRaise: boolean;
  isLoggedIn: boolean;
  lastName: string;
  firstName: string;
  studentId: string;
  handRaise: boolean;
  id: string;
  toggleStudentHandraise: (id: string) => void;
}

function StudentMenuItem({
  enableHandRaise,
  isStudentIdsVisible,
  lastName,
  firstName,
  studentId,
  isLoggedIn,
  handRaise,
  toggleStudentHandraise,
  id,
}: StudentMenuItemProps) {
  const studentName = `${lastName}, ${firstName}`;

  const ariaLabel = [
    studentName,
    ...(isStudentIdsVisible ? [`ID ${studentId}`] : []),
    ...(enableHandRaise
      ? [
          `${translate('Hand Raise')} ${
            handRaise ? translate('on') : translate('off')
          }`,
        ]
      : []),
  ];

  const handleOnClick = () => {
    if (!isLoggedIn || !enableHandRaise) return;

    toggleStudentHandraise(id);
  };

  return (
    <MenuItem onClick={handleOnClick} dismissOnClick={false}>
      {({ isActive, props }) => (
        <HandRaiseMenuItem
          isActive={isActive}
          a11yLabel={ariaLabel.join(', ')}
          studentId={studentId}
          name={studentName}
          isStudentIdsVisible={isStudentIdsVisible}
          allowHandraise={enableHandRaise}
          isPresent={isLoggedIn}
          isHandRaised={handRaise}
          {...props}
        />
      )}
    </MenuItem>
  );
}
